
import React, { useState, useEffect } from 'react';
import { BottomAppShowcase, PageShell } from './public/PublicPrimitives';
import { CleroS, CleroWordmarkSvg } from './Logo';
import Logo from './Logo';
import { FaApple, FaAndroid } from 'react-icons/fa';
import Lenis from 'lenis';
import { ANDROID_INVITE_URL, IOS_INVITE_URL } from '../constants/downloadLinks';

export { ANDROID_INVITE_URL, IOS_INVITE_URL };

const PrivacyPage: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const lastUpdated = 'October 13, 2026';

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.0,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PageShell
      background="bg-[#fafaf7]"
      description="Learn how Clero protects your personal, financial, and expense data."
      title="Privacy Policy | Clero"
    >
      {/* FLOATING / MORPHING HEADER */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isScrolled ? 'pt-3.5 px-4 sm:px-6' : 'pt-0 px-0'
        }`}
      >
        <header
          className={`pointer-events-auto w-full transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isScrolled
              ? 'max-w-[480px] rounded-[999px] bg-white/85 backdrop-blur-2xl border border-slate-200/80 px-4 sm:px-5 py-2 shadow-sm'
              : 'max-w-[720px] sm:max-w-[760px] rounded-[0px] bg-transparent backdrop-blur-none border-b border-slate-200/50 px-4 sm:px-6 py-3.5'
          }`}
        >
          <div className="flex items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-2 shrink-0">
              <a
                aria-label="Clero Home"
                className="flex items-center transition opacity-95 hover:opacity-100"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
              >
                <CleroWordmarkSvg height={24} color="#0D9488" />
              </a>
            </div>

            <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
              <button
                className="text-xs sm:text-sm font-bold text-slate-700 hover:text-slate-950 transition-colors whitespace-nowrap px-1 py-1"
                onClick={() => navigate('/support')}
                type="button"
              >
                Help Center
              </button>

              <button
                className="inline-flex items-center justify-center rounded-full bg-[var(--clero-primary)] px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold text-white transition-all hover:bg-[var(--clero-primary-dark)] shadow-sm active:scale-95 whitespace-nowrap"
                onClick={() => {
                  const el = document.getElementById('bottom-download');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                type="button"
              >
                Download App
              </button>
            </div>
          </div>
        </header>
      </div>

      <div className="h-16 sm:h-20" />

      {/* HERO SECTION */}
      <section className="pt-8 pb-10 sm:pb-14 px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center space-y-3">
          <span className="text-xs font-extrabold uppercase tracking-widest text-teal-600 block">
            Legal & Privacy
          </span>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-slate-950">
            Privacy Policy
          </h1>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider pt-1">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-6 px-4 sm:px-6">
        <div className="mx-auto max-w-3xl space-y-6">

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <p className="text-base sm:text-lg font-medium text-slate-900 leading-relaxed">
              Clero (“Clero”, “we”, “us”) respects your privacy and is committed to protecting your information.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Information We Collect</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              We collect only what is necessary to provide the Clero service:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li>Phone number and/or email address</li>
              <li>Basic usage information (e.g., split sessions, payment status)</li>
              <li>Payment-related tokens provided by our payment partners (such as Astra and Plaid)</li>
              <li>Connected-account transaction information provided by Plaid, including merchant, amount, date, category, account type, and whether a transaction is pending, posted, changed, refunded, or reversed</li>
              <li>Clero Rewards information, including offers shown or activated, qualifying purchases, reward calculations, redemption limits, cash-back status, and payout destination details such as bank name and masked account digits</li>
            </ul>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              We do not store raw card numbers, bank account numbers, or login credentials.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">How Payments Work</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Payments are processed securely by Clero's banking and payment partners. Clero only receives non-sensitive identifiers and tokens required to facilitate payments.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Fee Structure</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li>Payees currently pay no Clero fee for split payments.</li>
              <li>Organizer may be charged 1.0% of amount owed to them, capped at $2.99 per split session.</li>
              <li>Organizer coordination fee applies only when two or more participants owe in split.</li>
            </ul>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-6">
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-slate-950 tracking-tight">How We Use Your Information</h2>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                We use your information to:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
                <li>Facilitate split payments</li>
                <li>Match activated offers to qualifying purchases, calculate cash back, prevent duplicate or abusive redemptions, and deliver rewards</li>
                <li>Recommend relevant offers when Personalized offers is enabled</li>
                <li>Send payment links and notifications</li>
                <li>Provide customer support</li>
                <li>Improve our product</li>
              </ul>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                We do not sell your personal information.
              </p>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                In certain cases, our support team may access an account in a manner that simulates the user experience to diagnose and resolve issues. These actions are performed in a controlled environment and are logged for auditing purposes.
              </p>
            </div>

            <div className="pt-6 border-t border-slate-100 space-y-4">
              <h3 className="text-lg font-bold text-slate-950 tracking-tight">Contacts and Communication</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
                <li>To match contacts on Clero, we upload phone numbers only for secure matching.</li>
                <li>We do not upload contact names or extra address-book details for matching.</li>
                <li>If you provide your phone number, Clero may send essential transactional SMS messages.</li>
              </ul>
            </div>

            <div className="pt-6 border-t border-slate-100 space-y-4">
              <h3 className="text-lg font-bold text-slate-950 tracking-tight">SMS Communications</h3>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                If you provide your phone number, you consent to receive SMS messages from Clero related to your account, including one-time passcodes (OTP), payment notifications, and other essential transactional messages.
              </p>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                Message frequency varies. Message and data rates may apply.
              </p>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                You may opt out of receiving SMS messages at any time by contacting <a href="mailto:support@clero.money" className="text-teal-700 font-bold hover:underline">support@clero.money</a>.
              </p>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                We do not sell, rent, or share mobile phone numbers or SMS opt-in information with third parties for marketing or promotional purposes. We may share limited information with service providers solely to deliver transactional SMS messages on our behalf.
              </p>
            </div>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-6">
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-slate-950 tracking-tight">Clero Rewards</h2>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                Clero Rewards uses connected-card purchase information to operate card-linked offers. When you activate an offer, Clero may compare posted transactions with the offer's merchant, timing, purchase minimum, redemption limit, and other stated terms. Pending transactions do not create a final reward. Changes, refunds, or reversals may cause a reward to be adjusted, canceled, or reviewed.
              </p>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                For each potential or completed reward, we may maintain the matched transaction identifier, merchant, purchase amount and date, activated offer, cash-back rate and amount, qualification and review status, payout status, and an audit history. We use this information to calculate rewards, enforce offer limits, prevent duplicate payments and abuse, resolve disputes, provide support, and keep required financial records.
              </p>
            </div>

            <div className="pt-6 border-t border-slate-100 space-y-4">
              <h3 className="text-lg font-bold text-slate-950 tracking-tight">Personalized offers and your controls</h3>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                Personalized offers is on by default. When it is on, Clero may use patterns derived from your connected-card purchases—such as merchant and category frequency, recency, and spending ranges—to order offers and decide which relevant offer notifications to send. Clero may also use your own split history, offer value, and general exploration signals. Friends do not see your private transaction history through Rewards.
              </p>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                You can turn Personalized offers off at any time in the app under Rewards → Manage or Settings → Rewards preferences. After you turn it off, connected-card purchase history is no longer used to rank your offer feed or target personalized offer notifications. Clero may still show generally available offers and may use non-transaction signals, such as your own split activity and offer value, to organize them.
              </p>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                Turning personalization off does not deactivate offers, stop transaction processing needed to find qualifying purchases for offers you activated, cancel rewards, or delete transaction and reward records. Account deletion and data-deletion requests are handled separately as described below.
              </p>
            </div>

            <div className="pt-6 border-t border-slate-100 space-y-4">
              <h3 className="text-lg font-bold text-slate-950 tracking-tight">Cash-back payments</h3>
              <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                When a reward is confirmed, Clero may use Astra and our banking infrastructure to send cash back to your default connected bank account. We store provider identifiers, masked destination details, estimated arrival information, and transfer status so we can show progress and respond to failures. If a direct deposit cannot be completed, the reward may be placed in your claimable Clero balance until you complete required setup or select an eligible destination.
              </p>
            </div>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">How Rewards Data Is Shared</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Clero does not sell your transaction history. We do not share your individual purchase history with merchants for their advertising. We disclose only the information needed to operate Clero Rewards, comply with law, protect the service, or complete a service you request.
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li>Plaid provides connected-account and transaction data to Clero under your connection and consent.</li>
              <li>Astra and other banking or payment providers process reward payouts, identity verification, and related payment operations.</li>
              <li>Cloud hosting, security, analytics, error-monitoring, and notification providers may process limited data on Clero's behalf under service-provider restrictions.</li>
              <li>Merchants may receive aggregated or de-identified program reporting that is not intended to identify your individual purchase history.</li>
              <li>We may disclose information when required by law or reasonably necessary to investigate fraud, abuse, security incidents, or violations of our terms.</li>
            </ul>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Connected Gmail Data (Optional)</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              If you connect Gmail, Clero requests <span className="font-bold text-slate-900">read-only Gmail access</span> to help detect likely receipts and suggest splits.
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li>We only process likely receipt messages (for example receipts, invoices, and order confirmations).</li>
              <li>We do not send emails, and we do not modify or delete mailbox content.</li>
              <li>We do not use Gmail data for advertising and we do not sell Gmail-derived data.</li>
              <li>We use Gmail-derived data only to power receipt suggestions and user-initiated split flows.</li>
            </ul>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Temporary receipt files are deleted on dismiss, consume, or Gmail disconnect; untouched receipt artifacts expire after 30 days and are removed during the next daily cleanup.
            </p>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              You can disconnect Gmail at any time in the Clero app. On disconnect, OAuth access is revoked and Gmail-derived artifacts are purged from Clero systems.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Artificial Intelligence Data Disclosures</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Clero incorporates generative artificial intelligence (AI) models (such as Google Gemini / Vertex AI APIs) to power user-initiated expense features:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li><strong>Receipt & Screenshot Processing:</strong> When you upload a receipt image or checkout screenshot, the image is analyzed by AI models to extract merchant names, line items, subtotals, tax, tip, and totals.</li>
              <li><strong>Email Receipt Parsing:</strong> If Gmail integration is enabled, AI models parse text from receipt emails to suggest split items and totals automatically.</li>
              <li><strong>Merchant Information & Invites:</strong> Merchant names may be submitted to AI models to infer official domains for displaying merchant logos, and trip/booking parameters may be formatted into friendly invitation texts upon request.</li>
              <li><strong>Rewards Merchant Matching:</strong> If deterministic matching is inconclusive, limited merchant information from a transaction may be processed to help determine whether it corresponds to an activated offer. Clero does not send bank credentials, account numbers, balances, or your full transaction history for this matching step.</li>
            </ul>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              <strong>Data Safeguards & Model Non-Training:</strong> All data transmitted to AI models is transmitted securely via encrypted APIs, is processed solely to fulfill your specific request, is not sold, is not used for advertising, and is <strong>never retained or used by third-party AI providers to train foundation AI models</strong>.
            </p>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              <strong>Third-Party Provider Policy:</strong> For details on how our AI API provider processes and handles data, see <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-700 font-bold hover:underline">Google’s Privacy Policy</a>.
            </p>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              <strong>User Control:</strong> AI features are optional and user-initiated. You can choose not to upload screenshots or disconnect Gmail at any time in the Clero app.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Data Retention</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              We retain information only as long as needed to operate Clero or as required by law.
            </p>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Rewards transaction, activation, redemption, payout, and audit records may be retained as needed to administer offers, prevent duplicate rewards and fraud, handle refunds, reversals, disputes, and support, and meet legal, accounting, and financial-compliance obligations. Turning off Personalized offers stops future use of connected-card purchase history for personalization, but it does not by itself delete these records or previously derived recommendation data.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Deleting Your Data</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              You may delete your Clero account and associated data at any time. When you request deletion:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li>Your Clero account information is removed from our systems</li>
              <li>Access to linked payment providers is revoked</li>
              <li>Rewards personalization data and connected-account access are removed or de-identified, subject to records we must retain for legal, fraud-prevention, accounting, dispute, or transaction-integrity purposes</li>
            </ul>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              You may manage payment methods directly with our payment partners if desired.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Contact</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              For privacy questions or data deletion requests, contact:<br />
              <a href="mailto:privacy@clero.money" className="text-teal-700 font-bold hover:underline">privacy@clero.money</a>
            </p>
          </div>

          <div className="text-center py-4">
            <p className="text-xs text-slate-400 font-medium">This policy may be updated periodically.</p>
          </div>

        </div>
      </section>

      <div className="mt-12 mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8 mb-12">
        <BottomAppShowcase />
      </div>

      {/* FOOTER (MATCHING MAIN LANDING FOOTER) */}
      <footer className="border-t border-slate-200/80 bg-white px-4 py-12 sm:px-6 lg:px-8 mt-16">
        <div className="mx-auto max-w-[var(--clero-max-content)]">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-4 sm:col-span-2 lg:col-span-1">
              <img src="/clero_icon.png" alt="Clero" className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl shadow-sm object-cover" />
              <p className="text-xs font-semibold text-slate-400">
                Secured with direct bank connections via Plaid & Astra Financial.
              </p>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Compare</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare')} type="button">
                    All Splitting Apps
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/splitwise')} type="button">
                    Clero vs Splitwise
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/venmo')} type="button">
                    Clero vs Venmo
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/zelle')} type="button">
                    Clero vs Zelle
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Product</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/')} type="button">Features</button></li>
                <li><a className="transition hover:text-slate-950" href={IOS_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for iOS</a></li>
                <li><a className="transition hover:text-slate-950" href={ANDROID_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for Android</a></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Resources</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/blog')} type="button">Blog</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/support')} type="button">Help & Support</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/compare')} type="button">Compare Apps</button></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Legal & Privacy</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="text-teal-700 font-bold" onClick={() => navigate('/privacy')} type="button">Privacy Policy</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/terms')} type="button">Terms of Service</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/data-deletion')} type="button">Data Deletion</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 border-t border-slate-100 pt-6 text-center text-xs text-slate-400">
            <p>© 2026 Oryn Technologies Inc. All rights reserved. Clero is a financial technology platform, not a bank.</p>
          </div>
        </div>
      </footer>
    </PageShell>
  );
};

export default PrivacyPage;
